import { useNavigate } from 'react-router-dom';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { cleanHtmlContent } from '@member-portal/utils';
import { themeClasses, OptimizedImage } from '@member-portal/ui';
import type { CollectionListSnapshot } from '@/types/collectionList';

export interface HotelImage {
  id: string;
  url: string;
  description?: string;
  displayOrder?: number;
}

export interface Hotel {
  id: string;
  name: string;
  description?: string;
  websiteDescription?: string;
  benefits?: string;
  imageUrl?: string;
  images?: HotelImage[];
  city?: string;
  state?: string;
  country?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  vendorType?: string;
  recordType?: string;
  bookingInstructions?: string;
  termsConditions?: string;
  latitude?: number;
  longitude?: number;
  // Villa-specific fields
  bedrooms?: number;
  bathrooms?: number;
  features?: string;
  staffAndServices?: string;
  details?: string;
  specialNotes?: string;
  startingRate?: number;
  // Hotel fields
  exclusiveUse?: boolean;
}

interface HotelCardProps {
  hotel: Hotel;
  /** When set, returned via location.state so detail "Back to Collection" can restore the list. */
  collectionListSnapshot?: CollectionListSnapshot;
}

function getCollectionPropertyTypeLabel(recordType?: string): string {
  const rt = recordType?.toLowerCase() ?? '';
  if (rt.includes('villa')) return 'Villa';
  if (rt.includes('cruise')) return 'Cruise';
  if (rt.includes('hotel')) return 'Hotel';
  return recordType ?? '';
}

function HotelCard({ hotel, collectionListSnapshot }: HotelCardProps) {
  const navigate = useNavigate();

  const formatLocation = () => {
    const parts = [hotel.city, hotel.state, hotel.country].filter(Boolean);
    return parts.join(', ');
  };

  const description = hotel.websiteDescription || hotel.description || '';
  const cleanDescription = cleanHtmlContent(description);

  const handleClick = () => {
    navigate(`/collection/${hotel.id}`, {
      state: {
        hotel,
        ...(collectionListSnapshot ? { collectionList: collectionListSnapshot } : {}),
      },
    });
  };

  const propertyType = getCollectionPropertyTypeLabel(hotel.recordType);

  return (
    <div
      className={`group cursor-pointer ${themeClasses.common.card} ${themeClasses.animation.cardHover} overflow-hidden`}
      onClick={handleClick}
    >
      {/* Image */}
      <div className={`aspect-[4/3] ${themeClasses.common.imageContainer} relative`}>
        {hotel.imageUrl ? (
          <OptimizedImage
            src={hotel.imageUrl}
            alt={hotel.name}
            className={`${themeClasses.common.image} ${themeClasses.animation.imageHover}`}
            loading="lazy"
          />
        ) : (
          <div className={themeClasses.common.placeholderCenter}>
            <div className={themeClasses.common.placeholderGrid}>
              <div className={themeClasses.common.placeholderItem}></div>
              <div className={themeClasses.common.placeholderItem}></div>
              <div className={themeClasses.common.placeholderItem}></div>
              <div className={themeClasses.common.placeholderItem}></div>
            </div>
          </div>
        )}
        {/* Property Type Badge */}
        {hotel.recordType && (
          <span className={themeClasses.badge.imageOverlay}>
            {propertyType}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-2">
        <h3 className={`${themeClasses.title.h5} ${themeClasses.colors.hover.text} line-clamp-2`}>
          {hotel.name}
        </h3>

        {formatLocation() && (
          <div className={themeClasses.common.metadataItem}>
            <MapPinIcon className={`${themeClasses.common.metadataIcon} ${themeClasses.colors.text.muted}`} />
            <p className={themeClasses.text.mutedSmall}>
              {formatLocation()}
            </p>
          </div>
        )}

        {cleanDescription && (
          <p className={`${themeClasses.text.muted} line-clamp-3`}>
            {cleanDescription}
          </p>
        )}
      </div>
    </div>
  );
}

export default HotelCard;
